import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notifyService, SystemNotify } from '../services/notifyService';
import { useAuth } from './useAuth';

const POLL_INTERVAL_MS = 30_000;

interface NotifyContextValue {
  notifies: SystemNotify[];
  unreadCount: number;
  hasImportant: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const NotifyContext = createContext<NotifyContextValue | null>(null);

export const useNotifies = (): NotifyContextValue => {
  const ctx = useContext(NotifyContext);
  if (!ctx) {
    throw new Error('useNotifies must be used within NotifyProvider');
  }
  return ctx;
};

export const NotifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifies, setNotifies] = useState<SystemNotify[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasImportant, setHasImportant] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const data = await notifyService.getOwn({ only_unread: true, limit: 50 });
      setNotifies(data.notifies);
      setUnreadCount(data.unread_count);
      setHasImportant(data.has_important);
    } catch {
      // Silently ignore polling failures
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifies([]);
      setUnreadCount(0);
      setHasImportant(false);
      return;
    }
    refresh();
    intervalRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, refresh]);

  const markAsRead = useCallback(async (id: number) => {
    await notifyService.markAsRead(id);
    setNotifies((prev) => prev.filter((n) => n.id !== id));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    setHasImportant((prev) => {
      if (!prev) return false;
      const remaining = notifies.filter((n) => n.id !== id);
      return remaining.some((n) => n.typ === 'important');
    });
  }, [notifies]);

  const markAllRead = useCallback(async () => {
    await notifyService.markAllRead();
    setNotifies([]);
    setUnreadCount(0);
    setHasImportant(false);
  }, []);

  return (
    <NotifyContext.Provider value={{ notifies, unreadCount, hasImportant, markAsRead, markAllRead, refresh }}>
      {children}
    </NotifyContext.Provider>
  );
};
